/**
 * viewRoute — pure ViewKey <-> URL-hash mapping for the SPA shell.
 *
 * WHY: App.tsx switches views via local state with no browser-history
 * integration, so pressing Back exited the app entirely (P0 punch-list).
 * These helpers give App.tsx an Attio-style client-side history: each view
 * change pushes a `#/view` hash entry (pushState), and popstate restores
 * the view — Back/Forward navigate WITHIN Argo OS and never unmount the
 * app root.
 *
 * Hash-based (`#/dashboard`) rather than path-based so it works on any
 * static host with zero server rewrite rules, and never collides with the
 * dev `?crash=` query param.
 *
 * Pure + DOM-free so it unit-tests under the default node environment.
 */
import type { ViewKey } from '../components/TopMenu';

/** Every routable view. `satisfies` keeps each literal a valid ViewKey;
 *  the `_Missing` assertion below keeps the list EXHAUSTIVE — adding a
 *  ViewKey without routing it becomes a compile error. */
export const VIEW_KEYS = [
  'dashboard',
  'files',
  'contracts',
  'tools',
  'chat',
  'content-engine',
  'email-marketing',
  'contacts',
  'customer-success',
  'projects',
  'tickets',
  'goals',
  'hyper-coder',
  'scoring-engine',
  'reports',
  'admin',
] as const satisfies readonly ViewKey[];

type _Missing = Exclude<ViewKey, (typeof VIEW_KEYS)[number]>;
// If a ViewKey is ever added to TopMenu without being listed above,
// this line stops compiling.
const _assertExhaustive: _Missing extends never ? true : never = true;
void _assertExhaustive;

/** Type guard: is this unknown value a known ViewKey? */
export function isViewKey(value: unknown): value is ViewKey {
  return (
    typeof value === 'string' && (VIEW_KEYS as readonly string[]).includes(value)
  );
}

/** ViewKey -> location hash, e.g. 'tickets' -> '#/tickets'. */
export function viewToHash(view: ViewKey): string {
  return `#/${view}`;
}

/**
 * location.hash -> ViewKey, or null when the hash names no known view.
 * Tolerant of the shapes browsers/users produce: '#/tickets', '#tickets',
 * '/tickets', 'tickets', trailing slash, and ''.
 */
export function hashToView(hash: string): ViewKey | null {
  const cleaned = hash
    .replace(/^#/, '')
    .replace(/^\//, '')
    .replace(/\/$/, '')
    .trim();
  return isViewKey(cleaned) ? cleaned : null;
}

/**
 * Recover the ViewKey from a popstate event's `history.state` (which is
 * untyped and may be null, a foreign object, or our `{ view }` payload).
 */
export function viewFromHistoryState(state: unknown): ViewKey | null {
  if (state && typeof state === 'object' && 'view' in state) {
    const v = (state as { view: unknown }).view;
    if (isViewKey(v)) return v;
  }
  return null;
}
